import { useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import 'bootstrap-daterangepicker/daterangepicker.css';
import 'react-virtualized/styles.css';
import { ApexOptions } from 'apexcharts';
import _merge from 'lodash/merge';
import { AutoSizer, WindowScroller } from 'react-virtualized';
import { AdaptedMetricType } from '../monitoringTypes';
import { getMemoizedChartKey } from '../getMemoizedChartKey';
import { VirtualizedChartGrid } from './VirtualizedChartGrid';
import {
  ResponsiveVirtualizedChartGridProps,
  AutoSizerChildProps,
  MemoizedChartType,
  WindowScrollerChildProps,
} from '../DbMonitoringSchema';
import { TopProcess } from '../TopProcesses/TopProcess';
import { getChartComponent } from '../utils/chartUtils';
import { ViewAsEnum, DEFAULT_VIEW_AS } from '../constants';
import { filterChartByViewAs } from '../utils/filterChartByViewAs';

const getChartOptions = (
  chartType: string,
  chartOptions: ApexOptions,
): ApexOptions => {
  const chartOptionsMap = {
    line: chartOptions,
    area: {
      ..._merge(getAreaChartOptions(), chartOptions),
    } as ApexOptions,
  };

  return chartOptionsMap[chartType] || chartOptions;
};

export const ResponsiveVirtualizedChartGrid = (
  props: ResponsiveVirtualizedChartGridProps,
) => {
  const {
    monitoringMetrics,
    chartOptions,
    scrollRef,
    granularityControl,
    fullScreenChart,
    setFullScreenChart,
    startTime,
    endTime,
  } = props;

  // Extract values from wrapped props (used by DbMonitoring)
  const granularity = granularityControl?.granularity;
  const setGranularity = granularityControl?.setGranularity;

  // ViewAs state for filtering charts by aggregation type (AVG, MIN, MAX)
  // Default to AVG to show only AVG metrics in global view
  const [viewAsState] = useState<ViewAsEnum>(DEFAULT_VIEW_AS);

  const memoizedCharts: MemoizedChartType = {};

  useMemo(() => {
    monitoringMetrics?.forEach(
      (currMetric: AdaptedMetricType, index: number) => {
        const memoizationKey = getMemoizedChartKey(currMetric);
        if (!currMetric?.id) return;
        if (currMetric?.metricValues) {
          const process = monitoringMetrics?.find((item) => {
            return item?.id === currMetric?.title;
          });
          const memoizationValue = <TopProcess process={process} />;
          memoizedCharts[memoizationKey] = memoizationValue;
          return;
        }

        // Filter chart dataSets by aggregationType (only AVG for global view)
        // Fullscreen charts will use unfiltered data
        const filteredChart = filterChartByViewAs(currMetric, viewAsState);

        const {
          chart: ChartComponent,
          type,
          props: currChartProps,
        } = getChartComponent(filteredChart?.title);
        const chartCompOptions = getChartOptions(type, chartOptions);
        const memoizationValue = (
          <ChartComponent
            chart={filteredChart}
            chartOptions={chartCompOptions}
            granularityControl={
              granularity && setGranularity
                ? { granularity, setGranularity }
                : undefined
            }
            startTime={startTime}
            endTime={endTime}
            fullScreenChart={fullScreenChart}
            setFullScreenChart={setFullScreenChart}
            {...currChartProps}
          />
        );

        memoizedCharts[memoizationKey] = memoizationValue;
      },
    );
  }, [
    monitoringMetrics,
    chartOptions,
    granularity,
    setGranularity,
    startTime,
    endTime,
    fullScreenChart,
    setFullScreenChart,
    viewAsState,
  ]);

  const countOfValidCharts = Object?.keys?.(memoizedCharts)?.length;
  return (
    <>
      {countOfValidCharts ? (
        <WindowScroller
          scrollElement={scrollRef?.current}
          scrollingResetTimeInterval={0}
        >
          {(wsScrollerProps: WindowScrollerChildProps) => (
            <Stack
              flex="1 1 auto"
              width={scrollRef?.current?.clientWidth}
              height={scrollRef?.current?.clientHeight - 300}
            >
              <AutoSizer disableHeight>
                {(autoSizerProps: AutoSizerChildProps) => (
                  <VirtualizedChartGrid
                    {...props}
                    {...autoSizerProps}
                    {...wsScrollerProps}
                    memoizedCharts={memoizedCharts}
                  />
                )}
              </AutoSizer>
            </Stack>
          )}
        </WindowScroller>
      ) : (
        <></>
      )}
    </>
  );
};

export const getAreaChartOptions = (): ApexOptions => ({
  chart: {
    stacked: true,
    stackType: 'normal',
  },
  legend: {
    showForSingleSeries: true,
    onItemClick: {
      toggleDataSeries: false,
    },
    position: 'bottom',
    horizontalAlign: 'left',
    markers: {
      offsetX: -5,
      offsetY: 2,
    },
  },
  fill: {
    type: 'solid',
  },
});
